import { PERIOD_PRESET_ORDER, type PeriodPreset } from '@sl/core';

// Every word the date-range control renders (T-037, REQ-021). Same rule as the shell's `copy.ts`: a sentence
// with a key in `data/tables/message-keys.json` is never retyped here — the two refusals a custom range can
// earn are `validation.period.invalid_range` and `validation.period.before_history`, rendered through the
// catalogue. What is left are the control's own labels, written to the voice rules (DESIGN_SYSTEM §2) and
// checked against QA's glossary fixture by `glossary.test.tsx`.

/** REQ-021 AC1, in `PERIOD_PRESET_ORDER` — the one order the control, the parser and the prefetch share. */
export const PERIOD_LABEL: Record<PeriodPreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7d: 'Last 7 days',
  last_30d: 'Last 30 days',
  this_month: 'This month',
  last_month: 'Last month',
  custom: 'Custom',
};

/** The preset list's own label for the custom row — the mockup's "Custom…" (components.html row 6). */
export const CUSTOM_ROW_LABEL = 'Custom…';

/** The presets in the order the control lists them (`custom` last, as the mockup has it). */
export const PERIOD_PRESETS: readonly PeriodPreset[] = PERIOD_PRESET_ORDER;

/** Monday first, as the mockup's calendar is. Short form for the column heads, long form for their names. */
export const WEEKDAYS: readonly { short: string; long: string }[] = [
  { short: 'Mo', long: 'Monday' },
  { short: 'Tu', long: 'Tuesday' },
  { short: 'We', long: 'Wednesday' },
  { short: 'Th', long: 'Thursday' },
  { short: 'Fr', long: 'Friday' },
  { short: 'Sa', long: 'Saturday' },
  { short: 'Su', long: 'Sunday' },
];

export const PERIOD_COPY = {
  /** The popover's accessible name; the trigger says the range itself. */
  popoverTitle: 'Date range',
  presetsLabel: 'Ranges',
  calendarLabel: 'Pick your own dates',
  /** REQ-021 AC5 / REQ-261: the control says which zone the days belong to. */
  timeZoneNoteLead: 'Dates in',
  /** The trigger's tooltip — the mockup's own words (`_shared/web-shell.html`). */
  triggerHintLead: "Dates are in the ad account's time zone",
  compare: 'Compare to previous',
  compareHintOff: 'Compare every number with the period just before it.',
  /** Shown beside the resolved dates inside the control: "Compared with Sep 8 – 14". */
  comparedWith: 'Compared with',
  compareOffNote: 'Not compared with anything',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  apply: 'Apply',
  cancel: 'Cancel',
  pickStart: 'Pick a start date.',
  pickEnd: 'Now pick the end date.',
  /** REQ-021 AC2 / REQ-261 AC3 — a period that runs up to today is still filling up. */
  soFar: 'so far',
  /**
   * **"so far" keeps its word and changes its explainer** (REQ-021 AC6, D-704).
   *
   * A partial range is still partial, so the flag stays — but REQ-283 AC3's "still coming in" promises
   * MOTION, and on a day-old pull nothing is coming in. Where the served range ends on a day that is not the
   * device's own day, this is what "so far" means instead. No subject, so it reads correctly beside a day
   * chip and beside a month chip; parity-pinned with the phone.
   */
  soFarExplainer: 'Still filling up when we last heard from Meta.',
  loading: 'Loading your dates',
  /**
   * D-589 — the server answered a different range from the one the link asked for (a range running past the
   * last day this account has numbers for), so the control shows what is on screen and this says why in a
   * tooltip. The freshness label beside it ("Updated 2 days ago") is what explains how that happened.
   */
  servedLead: 'Showing the dates this account has numbers for.',
  /** The preferences read failed: the range still works, it just is not the one that was saved. */
  preferencesError: "We couldn't load your saved dates, so this is the usual range.",
} as const;

/** The footer checkbox names the length it is comparing against: "Compare to previous 7 days". */
export function compareLabel(days: number): string {
  return days === 1 ? 'Compare to the previous day' : `Compare to previous ${days} days`;
}

/** The switch's tooltip: the comparison dates are shown on hover / tap (REQ-021 AC3). */
export function compareHint(compareDates: string): string {
  return `${PERIOD_COPY.compare} · comparing with ${compareDates}`;
}

export function timeZoneNote(accountTimeZone: string): string {
  return `${PERIOD_COPY.timeZoneNoteLead} ${accountTimeZone}`;
}

export function triggerHint(accountTimeZone: string): string {
  return `${PERIOD_COPY.triggerHintLead} (${accountTimeZone})`;
}

/** D-589: "Showing the dates this account has numbers for. You asked for Sep 15 – 21." */
export function servedHint(asked: string): string {
  return `${PERIOD_COPY.servedLead} You asked for ${asked}.`;
}

/**
 * The words behind the time-zone note, in the shape `ui/Explainer` takes (T-045's contract: `explainerKey`,
 * `term`, `definition`, optional lines). The label the user sees is the note itself; the bubble says what a
 * "day" is here and, when the device is somewhere else, why the two clocks disagree (REQ-261 AC4).
 */
export const TIME_ZONE_EXPLAINER_KEY = 'dates-and-time-zone';

export interface TimeZoneExplainerWords {
  explainerKey: string;
  term: string;
  definition: string;
  lines: string[];
}

export function timeZoneExplainer(
  accountTimeZone: string,
  deviceTimeZone?: string,
): TimeZoneExplainerWords {
  const lines = [
    `Each day runs from midnight to midnight in ${accountTimeZone}, so "today" here is the same day Meta is counting.`,
  ];
  if (deviceTimeZone && deviceTimeZone !== accountTimeZone) {
    lines.push(
      `Your device is set to ${deviceTimeZone}, so the days here can start and end at a different moment on your own clock.`,
    );
  }
  return {
    explainerKey: TIME_ZONE_EXPLAINER_KEY,
    term: 'Dates and time zone',
    definition: `Every date in AdOrchestrator is a calendar day in your ad account's time zone, ${accountTimeZone}.`,
    lines,
  };
}
